import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { combineLatest, of } from 'rxjs';
import { filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { InsightsUserHomeShellComponent } from 'src/app/home/insights-user-home/insights-user-home-shell.component';
import { DashboardService, PdfPaperSize, UserDashboardService, UserStateService, UserVisualService, VisualService } from 'src/app/services/api.services';
import { VisualFileExportDialogService } from 'src/app/shared/visual-data-export/visual-data-export.service';
import { HomeSelectors } from '.';
import { DashboardPdfExportData, DashboardPdfExportDialog } from '../../dashboards/dialogs/pdf-export/pdf-export.dialog';
import { catchAppError } from '../../shared/utils';
import { AppActions, AppSelectors } from '../app';
import * as HomeActions from './home.actions';

@Injectable()
export class HomeEffects {
  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private dialog: MatDialog,
    private dashboardService: DashboardService,
    private visualService: VisualService,
    private userVisualService: UserVisualService,
    private visualExportService: VisualFileExportDialogService,
    private userDashboardService: UserDashboardService,
    private userStateService: UserStateService) { }


  load$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.load),
    tap(({ dashboardId, visualId }) => {
      this.store.dispatch(HomeActions.reset());
      this.store.dispatch(HomeActions.loadDashboards({ dashboardId }));
      this.store.dispatch(HomeActions.loadVisuals({ visualId }))
    })
  ), { dispatch: false });


  loadDashboards$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.loadDashboards),
    switchMap(({ dashboardId }) => this.dashboardService.listDashboards(true).pipe(
      map((dashboards) => HomeActions.loadDashboardsSuccess({ dashboards, dashboardId })),
      catchAppError(error => of(HomeActions.loadDashboardsFailure({ error })))
    )))
  );

  loadVisuals$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.loadVisuals),
    switchMap(({ visualId }) => this.visualService.listVisuals({
        includeLibrary: true,
        includeSamples: false,
        includeShared: true,
        includeContent: false
      }).pipe(
      map((visuals) => HomeActions.loadVisualsSuccess({ visuals, visualId })),
      catchAppError(error => of(HomeActions.loadVisualsFailure({ error })))
    )))
  );

  loadLastViewed$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.loadDashboardsSuccess, HomeActions.loadVisualsSuccess),
    withLatestFrom(
      this.store.pipe(select(HomeSelectors.isLoadingDashboards)),
      this.store.pipe(select(HomeSelectors.isLoadingVisuals)),
      this.store.pipe(select(HomeSelectors.getDashboards)),
      this.store.pipe(select(HomeSelectors.getVisuals)),
      this.store.pipe(select(AppSelectors.getUserState))),
    filter(([action, loadingDashboards, loadingVisuals, dashboards, visuals, userState]) => {
      return !loadingDashboards && !loadingVisuals;
    }),
    map(([action, loadingDashboards, loadingVisuals, dashboards, visuals, userState]) => {
      if (userState.lastViewedDashboardId && dashboards.find(d => (d.id === userState.lastViewedDashboardId && d.home))) {
        let dashboardId = userState.lastViewedDashboardId;

        if (action.type == HomeActions.loadDashboardsSuccess.type && action.dashboardId)
          dashboardId = action.dashboardId;

        return HomeActions.loadDashboard({ dashboardId });
      }

      if (userState.lastViewedVisualId && visuals.find(v => (v.id === userState.lastViewedVisualId && v.home))) {
        let visualId = userState.lastViewedVisualId;

        if (action.type == HomeActions.loadVisualsSuccess.type && action.visualId)
          visualId = action.visualId;

        return HomeActions.loadVisual({ visualId });
      }

      let homeDashboards = dashboards.filter(d => d.home);
      if (homeDashboards.length > 0) 
        return HomeActions.loadDashboard({ dashboardId: homeDashboards[0].id})

      let homeVisuals = visuals.filter(v => v.home);
      if (homeVisuals.length > 0) 
        return HomeActions.loadVisual({ visualId: homeVisuals[0].id})
      
      return HomeActions.setNoItem();
    })
  ));

  loadDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.loadDashboard),
    tap(({ dashboardId }) => this.store.dispatch(HomeActions.setLastViewedDashboardId({ dashboardId }))),
    switchMap(({ dashboardId }) => {
      return combineLatest([
        this.dashboardService.getDashboard(dashboardId),
        this.dashboardService.getDashboardVisualContent(dashboardId)
      ]).pipe(
        map(([dashboard, visualContents]) => {
          return HomeActions.loadDashboardSuccess({ dashboard, visualContents });
        }),
        catchAppError(error => of(HomeActions.loadDashboardFailure({ error })))
      );
    }))
  );

  // if we fail to load a dashboard due to a 404, revert to the first one
  resetDashboardOnLoadFailure$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.loadDashboardFailure),
    filter(err => err.error.status === HttpStatusCode.NotFound),
    withLatestFrom(this.store.select(HomeSelectors.getDashboards)),
    filter(([action, dashboards]) => !!dashboards?.length),
    map(([action, dashboards]) => {
      return HomeActions.loadDashboard({
        dashboardId: dashboards[0].id
      });
    }))
  );
  // same as above
  resetVisualOnLoadFailure$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.loadVisualFailure),
    filter(err => err.error.status === HttpStatusCode.NotFound),
    withLatestFrom(this.store.select(HomeSelectors.getVisuals)),
    filter(([action, visuals]) => !!visuals?.length),
    map(([action, visuals]) => {
      return HomeActions.loadVisual({
        visualId: visuals[0].id
      });
    }))
  );

  loadVisual$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.loadVisual),
    tap(({ visualId }) => this.store.dispatch(HomeActions.setLastViewedVisualId({ visualId }))),
    switchMap(({ visualId }) => {
      return this.visualService.getVisual(visualId, false).pipe(
        map((visual) => {
          return HomeActions.loadVisualSuccess({ visual });
        }),
        catchAppError(error => of(HomeActions.loadVisualFailure({ error })))
      );
    }))
  );

  setLoaded$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.loadVisualSuccess, HomeActions.loadDashboardSuccess, HomeActions.setNoItem),
    map (() => HomeActions.loaded())
  ))

  copyDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.copyDashboard),
    switchMap(({ dashboardId }) => this.dashboardService.copyDashboard(dashboardId).pipe(
      map((dashboard) => HomeActions.copyDashboardSuccess({ dashboard })),
      catchAppError(error => {
        error.showErrorSnack();
        return of(HomeActions.copyDashboardFailure({ error }));
      })
    ))
  ));


  setLastViewedDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.setLastViewedDashboardId),
    map(({ dashboardId }) => AppActions.updateUserState({
      userState: {
        lastViewedVisualId: undefined,
        lastViewedDashboardId: dashboardId
      }
    }))
  ));

  setLastViewedVisual$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.setLastViewedVisualId),
    map(({ visualId }) => AppActions.updateUserState({
      userState: {
        lastViewedDashboardId: undefined,
        lastViewedVisualId: visualId
      }
    }))
  ))

  // Navigate to edit view of the copy dashboard
  navigateAfterCopyDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.copyDashboardSuccess),
    map(({ dashboard }) => HomeActions.editDashboard({ dashboardId: dashboard.id })))
  );

  editDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.editDashboard),
    tap(({ dashboardId, editFilters }) => {
      this.router.navigate(['dashboards', dashboardId, 'edit'], {
        queryParams: {
          returnUrl: '/home',
          editFilters: editFilters ? true : undefined
        }
      });
    })),
    { dispatch: false }
  );

  copyVisual$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.copyVisual),
    switchMap(({ visualId }) => this.visualService.copyVisual({ visualId, copyContent: true }).pipe(
      map((visual) => HomeActions.copyVisualSuccess({ visual })),
      catchAppError(error => {
        error.showErrorSnack();
        return of(HomeActions.copyVisualFailure({ error }))
      })
    ))
  ));

  navigateAfterCopyVisual$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.copyVisualSuccess),
    map(({visual}) => {
      this.router.navigate(['/visuals', visual.id]);
    })),
  {dispatch: false}
  );

  addDashboard$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.addDashboard),
    switchMap(({ dashboardId }) => this.userDashboardService.updateUserDashboard({ dashboardId, home: true }).pipe(
      map(() => HomeActions.addDashboardSuccess({ dashboardId })),
      catchAppError(error => of(HomeActions.addDashboardFailure({ error })))
    ))
  ));

  loadDashboardAfterAdded$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.addDashboardSuccess),
    map(({ dashboardId }) => HomeActions.loadDashboard({ dashboardId }))
  ));

  addVisual$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.addVisual),
    switchMap(({ visualId }) => this.userVisualService.updateUserVisual({ visualId, home: true }).pipe(
      map(() => HomeActions.addVisualSuccess({ visualId })),
      catchAppError(error => of(HomeActions.addVisualFailure({ error })))
    ))
  ));

  loadVisualAfterAdded$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.addVisualSuccess),
    map(({ visualId }) => HomeActions.loadVisual({ visualId }))
  ))

  reloadAfterExternalChange$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.userDashboardOrVisualChanged),
    withLatestFrom(this.store.select(AppSelectors.pageComponent)),
    filter(([{ }, pageComponent]) => pageComponent == InsightsUserHomeShellComponent.pageId),
    map(() => HomeActions.load({ }))
  ));


  exportDashboardToPdf$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.exportDashboardToPdf),
    withLatestFrom(
      this.store.select(HomeSelectors.getDashboard),
      this.store.select(AppSelectors.getUser),
      this.store.select(AppSelectors.getUserState),
    ),
    tap(([{ filters }, dashboard, user, userState]) => {
      this.dialog.open(DashboardPdfExportDialog, {
        width: '500px',
        minHeight: '440px',
        disableClose: true,
        data: <DashboardPdfExportData>{
          dashboard,
          layoutOptions: user.roles.isAdmin,
          paperSize: userState?.defaultPdfPaperSize || PdfPaperSize.A4,
          filters
        }
      });
    })),
    { dispatch: false }
  );

  exportVisual$ = createEffect(() => this.actions$.pipe(
    ofType(HomeActions.exportVisual),
    withLatestFrom(
      this.store.select(HomeSelectors.getVisual)
    ),
    tap(([{ filters }, visual]) => {
      this.visualExportService.openDialog({
        visual,
        filters,
        renderUnshared: true,
      }, visual.name);
    })),
    {dispatch: false}
  )
}
